import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { springs } from '../lib/animations';
import { useAppContext } from '../context/AppContext';
import { countryFlag } from '../lib/country-utils';
import CedarLogo from './CedarLogo';

const navItems = [
  { path: '/', label: 'Home' },
  { path: '/rate', label: 'Rate' },
  { path: '/feed', label: 'Feed' },
  { path: '/games', label: 'Games' },
  { path: '/profile', label: 'Profile' },
] as const;

export default function Nav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { username, logout: ctxLogout } = useAppContext();
  const session = localStorage.getItem('jointRaterSession');
  const parsed = session ? JSON.parse(session) : null;

  const handleLogout = () => {
    localStorage.removeItem('jointRaterSession');
    ctxLogout();
    navigate('/');
  };

  const getPath = (item: typeof navItems[number]) => {
    if (item.path === '/profile' && username) {
      return `/profile/${encodeURIComponent(username)}`;
    }
    return item.path;
  };

  const isActive = (item: typeof navItems[number]) => {
    if (item.path === '/profile') return location.pathname.startsWith('/profile');
    if (item.path === '/') return location.pathname === '/';
    if (item.path === '/feed') return location.pathname === '/feed' || location.pathname === '/leaderboard';
    if (item.path === '/rate') return location.pathname === '/rate' || location.pathname === '/rate-bud' || location.pathname === '/battle';
    return location.pathname === item.path;
  };

  return (
    <>
      {/* Desktop: horizontal top bar */}
      <nav className="hidden md:block bg-cannabis-900 border-b border-cannabis-700/50 px-4 py-0">
        <div className="max-w-5xl mx-auto flex items-center justify-between h-12">
          <Link to="/feed" className="flex items-center gap-2 text-cannabis-300 hover:text-cannabis-200 transition">
            <CedarLogo size={22} />
            <span className="font-bold text-cream text-base">Hashups</span>
          </Link>

          <div className="flex items-center gap-0.5">
            {navItems.map(item => (
              <motion.div key={item.path} whileTap={{ scale: 0.95 }} transition={springs.bouncy}>
                <Link
                  to={getPath(item)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-200 relative ${
                    isActive(item)
                      ? 'text-warm bg-cannabis-800'
                      : 'text-cream/45 hover:text-cream hover:bg-cannabis-800/50'
                  }`}
                >
                  <span>{item.label}</span>
                  {isActive(item) && (
                    <motion.span
                      layoutId="desktopActiveTab"
                      className="absolute bottom-0 left-2 right-2 h-0.5 bg-warm rounded-full"
                      transition={springs.subtle}
                    />
                  )}
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {parsed?.isAdmin && (
              <Link to="/admin" className="text-cannabis-400/60 hover:text-cannabis-300 text-xs transition">
                Admin
              </Link>
            )}
            {username && (
              <button
                onClick={handleLogout}
                className="text-cream/25 hover:text-cream/50 text-xs transition px-2 py-1 rounded-lg hover:bg-cannabis-800"
              >
                Logout
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile: bottom tab bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-cannabis-900/95 backdrop-blur-sm border-t border-cannabis-700/40 z-50">
        <div className="flex items-center justify-around h-14 px-1">
          {navItems.map(item => (
            <motion.div key={item.path} whileTap={{ scale: 0.9 }} transition={springs.bouncy} className="flex-1">
              <Link
                to={getPath(item)}
                className={`flex flex-col items-center justify-center gap-0.5 py-1 rounded-lg transition-colors duration-200 relative ${
                  isActive(item) ? 'text-warm' : 'text-cannabis-300/35'
                }`}
              >
                <span className="text-[9px] font-medium leading-tight">{item.label}</span>
                {isActive(item) && (
                  <motion.span
                    layoutId="mobileActiveTab"
                    className="absolute -top-0.5 left-3 right-3 h-0.5 bg-warm rounded-full"
                    transition={springs.subtle}
                  />
                )}
              </Link>
            </motion.div>
          ))}
        </div>
        {parsed?.isAdmin && (
          <div className="absolute -top-6 right-2">
            <Link
              to="/admin"
              className="text-cannabis-400/40 hover:text-cannabis-300 text-[10px] bg-cannabis-900 px-2 py-0.5 rounded-t border border-b-0 border-cannabis-700"
            >
              Admin
            </Link>
          </div>
        )}
      </nav>

      <div className="md:hidden h-14" />
    </>
  );
}
